import { MorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.transcription";
import {
  PinyinSoundButton,
  usePinyinSoundButtonGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-sound-button";
import { useMorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { useGraphemePanelsMnemonicsTabPress } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._context";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";

export type MorphemeMnemonicsPanelTranscriptionTabButtonProps = {
  focused: boolean;
  pinyin: string;
  pinyinKey: string | undefined;
  tabIndex: number;
};

/**
 * Pinyin transcription tab. The whole tab area activates the tab when
 * pressed; when focused and a `pinyinKey` is provided, an inner
 * `PinyinSoundButton` consumes its own press to play audio. Pressing the tab
 * away from the icon does NOT play audio.
 */
export function MorphemeMnemonicsPanelTranscriptionTabButton({
  focused,
  pinyin,
  pinyinKey,
  tabIndex,
}: MorphemeMnemonicsPanelTranscriptionTabButtonProps) {
  const transcription = useMorphemeTranscription(pinyin);
  const onPress = useGraphemePanelsMnemonicsTabPress()(tabIndex);
  const [readonlySlice] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data,
  );
  const soundButtonGate = usePinyinSoundButtonGate(readonlySlice, pinyinKey);

  return (
    <Pressable
      accessibilityLabel={pinyin}
      accessibilityRole="tab"
      accessibilityState={focused ? { selected: true } : {}}
      className="h-12 flex-1"
      onPress={onPress}
    >
      <View
        className={cn(
          "h-full flex-1 items-center justify-center border-t-2",
          focused ? "border-t-black" : "mx-1.5 border-t-black/15",
        )}
      >
        <View className="flex-row items-center gap-1">
          <MorphemeTranscription
            className={cn(
              "text-base font-medium",
              focused ? "text-foreground" : "text-muted-foreground",
            )}
            numberOfLines={1}
            transcription={transcription}
          />
          {focused && pinyinKey !== undefined && soundButtonGate !== null && (
            <PinyinSoundButton gate={soundButtonGate} iconClassName="text-foreground" size={14} />
          )}
        </View>
      </View>
    </Pressable>
  );
}
